import {
  collection,
  CollectionReference,
  doc,
  DocumentReference,
  query,
  where,
} from 'firebase/firestore';
import chunk from 'lodash/chunk';
import flatten from 'lodash/flatten';
import isEmpty from 'lodash/isEmpty';
import uniqBy from 'lodash/uniqBy';
import { collectionData, docData } from 'rxfire/firestore';
import { combineLatest, EMPTY, Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { IPermission, ITeam, IUser } from '../types';
import { entitiesProvider, IEntities } from './entitiesProvider';
import { authenticatedUserProvider } from './userProvider';

/**
 * @description observes the user accounts the user has access to
 * @param user
 * @returns
 */

export interface IPermissionDescription {
  teamName: string;
  role: string;
  id: string;
}
export interface ICoach {
  name: string;
  email: string;
  teamCount: number;
  permissions?: IPermission[];
  id: string;
}

const coachesProfileCollection = (user: IUser): Observable<IUser[]> => {
  if (isEmpty(user)) return EMPTY;
  if (user.claims?.orgAdmin) {
    const orgAdminQuery = query(
      collection(
        user.services.firestore,
        'userProfiles'
      ) as CollectionReference<IUser>,
      where('orgId', '==', user.orgId),
      where('id', '!=', user.id)
    );
    return collectionData(orgAdminQuery, { idField: 'id' });
  } else {
    const allowedTeams = user.permissions.map(
      (perm: IPermission): string => perm.id
    );
    if (allowedTeams.length <= 10) {
      const mQuery = query(
        collection(
          user.services.firestore,
          'userProfiles'
        ) as CollectionReference<IUser>,
        where('orgId', '==', user.orgId),
        where('teams', 'array-contains-any', allowedTeams),
        where('id', '!=', user.id)
      );
      return collectionData(mQuery, { idField: 'id' });
    } else {
      const batches = chunk(allowedTeams, 10);
      const queries = batches.map((batch: string[]): Observable<IUser[]> => {
        return collectionData(
          query(
            collection(
              user.services.firestore,
              'userProfiles'
            ) as CollectionReference<IUser>,
            where('orgId', '==', user.orgId),
            where('teams', 'array-contains-any', batch),
            where('id', '!=', user.id)
          ),
          { idField: 'id' }
        );
      });
      return combineLatest(queries).pipe(
        map((result: IUser[][]): IUser[] =>
          /// there can be duplicate results here so we need to ensure uniqueness
          uniqBy(flatten(result), (mUser: IUser): string => mUser.id)
        )
      );
    }
  }
};

export const coachesProvider = (): Observable<ICoach[]> =>
  authenticatedUserProvider().pipe(
    switchMap(
      (user: IUser): Observable<IUser[]> => coachesProfileCollection(user)
    ),
    map((users: IUser[]): ICoach[] => {
      return users
        .filter((user: IUser): boolean => user.teams?.length > 0)
        .map((user: IUser): ICoach => {
          return {
            id: user.id,
            name: user.name,
            email: user.email,
            teamCount: user.teams?.length ?? 0,
          };
        });
    })
  );

/**
 * @description listens to the permissions for the supplied user
 * @param coach
 * @returns
 */
export const permissionsProvider = (coach: ICoach): Observable<ICoach> => {
  return authenticatedUserProvider().pipe(
    switchMap((requestingUser: IUser): Observable<ICoach> => {
      if (isEmpty(requestingUser)) return EMPTY;
      let permissions$: Observable<IPermission[]>;
      if (requestingUser.claims?.orgAdmin) {
        const orgAdminQuery = query(
          collection(
            requestingUser.services.firestore,
            `userProfiles/${coach.id}/teams`
          ) as CollectionReference<IPermission>,
          where('orgId', '==', requestingUser.orgId)
        );
        permissions$ = collectionData<IPermission>(orgAdminQuery, {
          idField: 'id',
        });
      } else {
        const allowedTeams = requestingUser.permissions
          .filter((perm: IPermission): boolean => perm.write)
          .map((perm: IPermission): string => perm.id);
        const obs = allowedTeams.map(
          (teamId: string): Observable<IPermission> => {
            return docData(
              doc(
                requestingUser.services.firestore,
                `userProfiles/${coach.id}/teams/${teamId}`
              ) as DocumentReference<IPermission>
            );
          }
        );
        permissions$ = combineLatest(obs).pipe(
          map((perms: IPermission[]): IPermission[] =>
            /// filter empty permissions
            perms.filter((perm: IPermission): boolean => !!perm?.id)
          )
        );
      }
      return combineLatest([
        permissions$.pipe(
          map(
            (perms: IPermission[]): ICoach => ({ ...coach, permissions: perms })
          )
        ),
        entitiesProvider().pipe(
          map((entities: IEntities): ITeam[] => entities.teams)
        ),
      ]).pipe(
        map((results: [ICoach, ITeam[]]): ICoach => {
          const [user, teams] = results;
          const mTeams = user.permissions
            .map((perm: IPermission): IPermission => {
              const team = teams.find(
                (mTeam: ITeam): boolean => mTeam.id === perm.id
              );
              if (!team) return null;
              const role = perm.write ? 'Team Admin' : 'Team User';
              return {
                ...perm,
                role,
                teamName: team.name,
              };
            })
            /// filter null results
            .filter((value: IPermission): boolean => !!value);
          return {
            id: user.id,
            name: user.name,
            email: user.email,
            permissions: mTeams,
            teamCount: mTeams.length,
          };
        })
      );
    })
  );
};
